// Optimistic local application of every operation (Guide §10.7): the app updates its own mirror at once so the shop never waits for the
// network. Numeric consequences (stock, balances) are NOT written here — they come from the overlay, recomputed from the outbox.
import {
  D, Decimal, ZERO, addDaysToDate, averageCost, computeReturn, computeSale, derivedId, diffDays, money, round2,
  type OpPayload, type OpType, type ModuleOpType, type DraftLine,
} from '@dd/shared';
import type { ApplyCtx } from './ctx';
import type * as T from '@/db/types';
import { MODULE_APPLY, MODULE_GUARDS } from './applyModules';

type Handler<K extends OpType> = (c: ApplyCtx, p: OpPayload<K>, op: Pick<T.OutboxItem, 'opId' | 'entityId' | 'clientTs' | 'baseVersion' | 'actor' | 'changedFields'>) => Promise<void>;
type Op = Parameters<Handler<'sale.create'>>[2];

const f2 = (v: Decimal.Value): string => money(v).toFixed(2);
const f3 = (v: Decimal.Value): string => D(v).toDecimalPlaces(3).toFixed(3);

// ── sales ────────────────────────────────────────────────────────────────────
const saleCreate: Handler<'sale.create'> = async (c, p, op) => {
  const d = c.d;
  const prods = new Map<string, T.Product>();
  for (const l of p.lines) if (l.productId && !prods.has(l.productId)) {
    const pr = (await d.products.get(l.productId)) as T.Product | undefined;
    if (pr) prods.set(l.productId, pr);
  }
  const draft: DraftLine[] = p.lines.map((l) => ({
    qty: l.qty, unitPrice: l.unitPrice, unitFactor: l.unitFactor, discount: l.discount ?? null,
    costPrice: l.productId ? (prods.get(l.productId)?.costPrice ?? null) : null,
  }));
  const t = computeSale({ lines: draft, discount: p.discount ?? null, payments: p.payments.map((x) => ({ method: x.method, amount: x.amount })), hasCustomer: !!p.customerId }, { rounding: p.rounding, roundingStep: p.roundingStep, roundingMode: p.roundingMode });
  const total = D(p.claimed.total);
  const due = D(p.claimed.dueAmount);
  const settings = ((await d.settings.toCollection().first()) as T.ShopSettingsRow | undefined)?.settings;
  await c.put<T.Sale>('sales', {
    id: op.entityId, shopId: c.item.shopId, invoiceNo: p.invoiceNo, deviceCode: p.deviceCode, deviceSeq: p.deviceSeq, customerId: p.customerId ?? null, status: 'completed',
    subtotal: f2(t.subtotal), lineDiscountTotal: f2(t.lineDiscountTotal), saleDiscountType: p.discount?.type ?? null, saleDiscountValue: p.discount ? f2(p.discount.value) : null,
    saleDiscountAmount: f2(t.saleDiscountAmount), roundingAdj: f2(t.roundingAdj), total: f2(total), paidTotal: f2(t.paidTotal), dueAmount: f2(due),
    costTotal: t.costTotal ? f2(t.costTotal) : null, grossProfit: t.grossProfit ? f2(t.grossProfit) : null, isLoss: t.isLoss, promotionId: p.promotionId ?? null,
    linkToken: p.linkToken, smsStatus: 'none', replacesSaleId: p.replacesSaleId ?? null, note: p.note ?? null, bizDate: c.bizDate, clientTs: op.clientTs,
    createdById: op.actor.id, seasonId: p.seasonId ?? null, _local: op.opId, _pending: 1,
  });
  for (let i = 0; i < p.lines.length; i++) {
    const l = p.lines[i]!;
    const lt = t.lines[i]!;
    await c.put<T.SaleItem>('sale_items', {
      id: l.id, saleId: op.entityId, productId: l.productId ?? null, variantId: l.variantId ?? null, batchId: l.batchId ?? null, serialNo: l.serialNo ?? null,
      serialId: l.serialNo ? ((await d.serial_units.where('serialNo').equals(l.serialNo).first())?.id ?? null) : null,
      nameSnapshot: l.nameSnapshot, unit: l.unit, unitFactor: String(l.unitFactor), qty: f3(l.qty), unitPrice: f2(l.unitPrice), lineGross: f2(lt.lineGross), lineDiscount: f2(lt.lineDiscount),
      allocSaleDiscount: f2(lt.allocSaleDiscount), lineNet: f2(lt.lineNet), unitCost: lt.unitCost ? f2(lt.unitCost) : null, lineCost: lt.lineCost ? f2(lt.lineCost) : null,
      lineProfit: lt.lineProfit ? f2(lt.lineProfit) : null, isLoss: lt.isLoss, returnedQty: '0.000',
    });
  }
  let n = 0;
  let left = t.total;
  for (const pay of p.payments) {
    const amount = Decimal.min(D(pay.amount), left);
    if (amount.lte(0)) continue;
    left = left.minus(amount);
    await c.put<T.SalePayment>('sale_payments', { id: pay.id, saleId: op.entityId, method: pay.method, amount: f2(amount), moneyAccountId: pay.moneyAccountId, reference: pay.reference ?? null });
    await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(++n), moneyAccountId: pay.moneyAccountId, amount: f2(amount), reason: 'sale_payment', refType: 'sale', refId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
  if (due.gt(0) && p.customerId) {
    const dueDate = p.dueDate ?? addDaysToDate(c.bizDate, settings?.defaultDueDays ?? 30);
    await c.addLocal<T.LedgerEntry>('customer_ledger', { id: c.localId(++n), customerId: p.customerId, type: 'sale_credit', amount: f2(due), dueDate, saleId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
    if (p.installments?.length) {
      const planId = c.localId(++n);
      await c.addLocal<T.InstallmentPlan>('installment_plans', { id: planId, customerId: p.customerId, saleId: op.entityId, total: f2(due) });
      let seq = 1;
      for (const ins of [...p.installments].sort((a, b) => a.dueDate.localeCompare(b.dueDate))) {
        await c.addLocal<T.Installment>('installments', { id: c.localId(++n), planId, seq: seq++, amount: f2(ins.amount), dueDate: ins.dueDate, paid: '0.00' });
      }
    }
  }
  for (let i = 0; i < p.lines.length; i++) {
    const l = p.lines[i]!;
    const pr = l.productId ? prods.get(l.productId) : undefined;
    if (!pr?.trackStock) continue;
    await c.addLocal<T.StockMovement>('stock_movements', { id: c.localId(++n), productId: pr.id, variantId: l.variantId ?? null, batchId: l.batchId ?? null, qty: f3(D(l.qty).times(l.unitFactor).negated()), unitCost: pr.costPrice ?? '0', reason: 'sale', refType: 'sale', refId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
  // a delivered repair job becomes "delivered" together with its sale (electronics, Bible 3.x)
  if (p.replacesSaleId) { /* the replaced sale is voided by its own op */ }
};

/** The money a voided / restored bill moves, shown at once (the server writes the same rows: reason sale_void / sale_restore). */
const saleMoneyRows = async (c: ApplyCtx, op: Op, saleId: string, reason: 'sale_void' | 'sale_restore'): Promise<void> => {
  let n = 0;
  for (const pay of (await c.d.sale_payments.where('saleId').equals(saleId).toArray()) as T.SalePayment[]) {
    const amount = reason === 'sale_void' ? D(pay.amount).negated() : D(pay.amount);
    await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(++n), moneyAccountId: pay.moneyAccountId, amount: f2(amount), reason, refType: 'sale', refId: saleId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
};

const saleVoid: Handler<'sale.void'> = async (c, p, op) => {
  const s = (await c.d.sales.get(op.entityId)) as T.Sale | undefined;
  if (!s) return;
  await c.patch('sales', s.id, { status: 'voided', voidedAt: op.clientTs, voidReason: p.reason, _pending: 1 });
  for (const pl of await c.d.installment_plans.where('saleId').equals(s.id).toArray()) {
    for (const i of await c.d.installments.where('planId').equals(pl.id).toArray()) await c.remove('installments', i.id);
    await c.remove('installment_plans', pl.id);
  }
  await saleMoneyRows(c, op, s.id, 'sale_void');
};

const saleRestore: Handler<'sale.restore'> = async (c, _p, op) => {
  const s = await c.patch('sales', op.entityId, { status: 'completed', voidedAt: null, voidReason: null, _pending: 1 });
  if (s) await saleMoneyRows(c, op, op.entityId, 'sale_restore');
};

// ── returns ──────────────────────────────────────────────────────────────────
const returnCreate: Handler<'return.create'> = async (c, p, op) => {
  const d = c.d;
  const sale = (await d.sales.get(p.saleId)) as T.Sale | undefined;
  if (!sale) return;
  const items = (await d.sale_items.where('saleId').equals(sale.id).toArray()) as T.SaleItem[];
  const priorRets = (await d.sale_returns.where('saleId').equals(sale.id).toArray()).filter((r) => !r.voidedAt);
  const priorItems = (await d.sale_return_items.where('returnId').anyOf(priorRets.map((r) => r.id)).toArray()) as T.SaleReturnItem[];
  const res = computeReturn(
    items.map((i) => {
      const mine = priorItems.filter((x) => x.saleItemId === i.id);
      return {
        saleItemId: i.id, qtySold: i.qty, returnedQty: i.returnedQty, lineNet: i.lineNet, lineCost: i.lineCost ?? null,
        netReversedSoFar: mine.reduce((a, x) => a.plus(x.netReversed), ZERO).toFixed(2), costReversedSoFar: mine.reduce((a, x) => a.plus(x.costReversed ?? 0), ZERO).toFixed(2),
      };
    }),
    p.lines.map((l) => ({ saleItemId: l.saleItemId, qty: l.qty, restock: l.restock })),
    sale.dueAmount,
  );
  const exchange = !!p.exchangeSaleId;
  const refund = exchange ? ZERO : res.refundCash;
  await c.put<T.SaleReturn>('sale_returns', {
    id: op.entityId, saleId: sale.id, returnNo: p.returnNo, reason: p.reason, refundCash: f2(res.refundCash), bakiAdjust: f2(res.bakiAdjust), exchangeSaleId: p.exchangeSaleId ?? null,
    moneyAccountId: refund.gt(0) ? (p.moneyAccountId ?? null) : null, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id, _local: op.opId,
  });
  for (const rl of res.lines) {
    const reqLine = p.lines.find((x) => x.saleItemId === rl.saleItemId)!;
    await c.put<T.SaleReturnItem>('sale_return_items', { id: reqLine.id, returnId: op.entityId, saleItemId: rl.saleItemId, qty: f3(rl.qty), restock: rl.restock, netReversed: f2(rl.netReversed), costReversed: rl.costReversed ? f2(rl.costReversed) : null });
    const it = items.find((x) => x.id === rl.saleItemId)!;
    await c.patch('sale_items', it.id, { returnedQty: f3(D(it.returnedQty).plus(rl.qty)) });
  }
  let n = 0;
  if (res.bakiAdjust.gt(0) && sale.customerId) {
    await c.addLocal<T.LedgerEntry>('customer_ledger', { id: c.localId(++n), customerId: sale.customerId, type: 'return_credit', amount: f2(res.bakiAdjust.negated()), saleId: sale.id, returnId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
  if (refund.gt(0) && p.moneyAccountId) {
    await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(++n), moneyAccountId: p.moneyAccountId, amount: f2(refund.negated()), reason: 'refund', refType: 'return', refId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
};

const returnVoid: Handler<'return.void'> = async (c, _p, op) => {
  const d = c.d;
  const r = (await d.sale_returns.get(op.entityId)) as T.SaleReturn | undefined;
  if (!r) return;
  await c.patch('sale_returns', r.id, { voidedAt: op.clientTs });
  for (const rl of (await d.sale_return_items.where('returnId').equals(r.id).toArray()) as T.SaleReturnItem[]) {
    const it = (await d.sale_items.get(rl.saleItemId)) as T.SaleItem | undefined;
    if (it) await c.patch('sale_items', it.id, { returnedQty: f3(Decimal.max(D(it.returnedQty).minus(rl.qty), ZERO)) });
  }
  // the refunded cash comes back into the drawer (server: reason refund_void)
  if (r.moneyAccountId && D(r.refundCash).gt(0) && !r.exchangeSaleId) {
    await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(1), moneyAccountId: r.moneyAccountId, amount: f2(r.refundCash), reason: 'refund_void', refType: 'return', refId: r.id, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
};

// ── baki ─────────────────────────────────────────────────────────────────────
const ledger = async (c: ApplyCtx, op: Op, n: number, e: Omit<T.LedgerEntry, 'id' | 'bizDate' | 'clientTs' | 'actorId'>): Promise<void> =>
  c.addLocal<T.LedgerEntry>('customer_ledger', { ...e, id: c.localId(n), bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });

const bakiCredit: Handler<'baki.credit'> = async (c, p, op) => {
  await ledger(c, op, 1, { customerId: p.customerId, type: p.kind, amount: f2(p.amount), dueDate: p.dueDate ?? null, note: p.note ?? null });
};
const bakiPayment: Handler<'baki.payment'> = async (c, p, op) => {
  await ledger(c, op, 1, { customerId: p.customerId, type: 'payment', amount: f2(D(p.amount).negated()), dueDate: p.dueDate ?? null, saleId: p.saleId ?? null, installmentId: p.installmentId ?? null, method: p.method, moneyAccountId: p.moneyAccountId, note: p.note ?? null });
  await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(2), moneyAccountId: p.moneyAccountId, amount: f2(p.amount), reason: 'baki_collection', refType: 'customer', refId: p.customerId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  if (p.installmentId) {
    const ins = (await c.d.installments.get(p.installmentId)) as T.Installment | undefined;
    if (ins) await c.patch('installments', ins.id, { paid: f2(Decimal.min(D(ins.paid).plus(p.amount), D(ins.amount))) });
  }
};
const bakiWriteoff: Handler<'baki.writeoff'> = async (c, p, op) => {
  await ledger(c, op, 1, { customerId: p.customerId, type: 'writeoff', amount: f2(D(p.amount).negated()), note: p.note ?? null });
  if (p.markDoubtful) await c.patch('customers', p.customerId, { isDoubtful: true });
};

// ── customers ────────────────────────────────────────────────────────────────
const customerUpsert: Handler<'customer.upsert'> = async (c, p, op) => {
  const ex = (await c.d.customers.get(op.entityId)) as T.Customer | undefined;
  const base = ex ?? ({ id: op.entityId, shopId: c.item.shopId, balance: '0.00', version: 1, isDoubtful: false, smsOptOut: false, createdAt: op.clientTs } as T.Customer);
  const next: T.Customer = {
    ...base, name: p.name, phone: p.phone ?? null, address: p.address ?? null, note: p.note ?? null, tier: p.tier ?? null,
    creditLimit: p.creditLimit ?? null, isDoubtful: p.isDoubtful ?? base.isDoubtful, smsOptOut: p.smsOptOut ?? base.smsOptOut, photoUrl: p.photoUrl !== undefined ? p.photoUrl : base.photoUrl ?? null, deletedAt: null, _pending: 1,
  };
  await c.put('customers', next);
};
const customerDelete: Handler<'customer.delete'> = async (c, _p, op) => {
  await c.patch('customers', op.entityId, { deletedAt: op.clientTs, _pending: 1 });
};

// ── products ─────────────────────────────────────────────────────────────────
const productUpsert: Handler<'product.upsert'> = async (c, p, op) => {
  const d = c.d;
  const ex = (await d.products.get(op.entityId)) as T.Product | undefined;
  const cost = p.costPrice ?? (ex ? ex.costPrice : p.opening?.unitCost) ?? null;
  const row: T.Product = {
    ...(ex ?? ({ id: op.entityId, shopId: c.item.shopId, onHand: '0.000', version: 1, createdAt: op.clientTs } as T.Product)),
    name: p.name, baseUnit: p.baseUnit, sellPrice: f2(p.sellPrice), costPrice: cost === null ? null : f2(cost), trackStock: p.trackStock, reorderLevel: p.reorderLevel ?? null, barcode: p.barcode ?? null,
    tags: p.tags, medicineId: p.medicineId ?? null, hasVariants: p.hasVariants || !!p.variants?.length, hasBatches: p.hasBatches, hasSerials: p.hasSerials, attrs: p.attrs, deletedAt: null, _pending: 1,
  };
  await c.put('products', row);
  if (p.units) {
    const keep = new Set(p.units.map((u) => u.id));
    for (const o of await d.product_units.where('productId').equals(op.entityId).toArray()) if (!keep.has(o.id)) await c.remove('product_units', o.id);
    for (const u of p.units) await c.put<T.ProductUnit>('product_units', { id: u.id, productId: op.entityId, unitName: u.unitName, factor: String(u.factor), sellPrice: u.sellPrice ?? null, barcode: u.barcode ?? null });
  }
  if (p.tiers) {
    const keep = new Set(p.tiers.map((u) => u.id));
    for (const o of await d.price_tiers.where('productId').equals(op.entityId).toArray()) if (!keep.has(o.id)) await c.remove('price_tiers', o.id);
    for (const t of p.tiers) await c.put<T.PriceTier>('price_tiers', { id: t.id, productId: op.entityId, tier: t.tier, minQty: t.minQty ?? null, price: f2(t.price) });
  }
  if (p.variants) {
    for (const v of p.variants) {
      const old = (await d.product_variants.get(v.id)) as T.ProductVariant | undefined;
      await c.put<T.ProductVariant>('product_variants', { id: v.id, productId: op.entityId, attrs: v.attrs, barcode: v.barcode ?? null, sellPrice: v.sellPrice ?? null, onHand: old?.onHand ?? '0.000', deletedAt: v.deleted ? op.clientTs : null });
    }
  }
  // opening stock (create only): same derived ids as the server
  if (!ex && p.opening && D(p.opening.qty).gt(0)) {
    const o = p.opening;
    let batchId: string | null = null;
    if (p.hasBatches || o.batchNo || o.expiryDate) {
      batchId = c.next(derivedId);
      await c.put<T.StockBatch>('stock_batches', { id: batchId, productId: op.entityId, batchNo: o.batchNo ?? null, expiryDate: o.expiryDate ?? null, costPrice: f2(o.unitCost), onHand: '0.000', receivedAt: op.clientTs });
    }
    if (p.hasSerials) {
      for (const sn of o.serialNos ?? []) {
        await c.put<T.SerialUnit>('serial_units', { id: c.next(derivedId), productId: op.entityId, serialNo: sn, status: 'in_stock', costPrice: f2(o.unitCost) });
      }
    }
  }
};
const productDelete: Handler<'product.delete'> = async (c, _p, op) => {
  await c.patch('products', op.entityId, { deletedAt: op.clientTs, _pending: 1 });
};
const stockAdjust: Handler<'stock.adjust'> = async (c, p, op) => {
  const pr = (await c.d.products.get(p.productId)) as T.Product | undefined;
  if (!pr?.trackStock) return;
  await c.addLocal<T.StockMovement>('stock_movements', { id: c.localId(1), productId: p.productId, variantId: p.variantId ?? null, batchId: p.batchId ?? null, qty: '0.000', unitCost: pr.costPrice ?? '0', reason: p.mode === 'count' ? 'adjustment_count' : p.mode === 'damage' ? 'adjustment_damage' : 'adjustment_expired', refType: 'adjustment', refId: op.opId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
};

// ── vendors & purchases ──────────────────────────────────────────────────────
const vendorUpsert: Handler<'vendor.upsert'> = async (c, p, op) => {
  const ex = (await c.d.vendors.get(op.entityId)) as T.Vendor | undefined;
  await c.put<T.Vendor>('vendors', { ...(ex ?? { id: op.entityId, payable: '0.00', version: 1 }), name: p.name, phone: p.phone ?? null, address: p.address ?? null, note: p.note ?? null, deletedAt: null, _pending: 1 });
};

const purchaseCreate: Handler<'purchase.create'> = async (c, p, op) => {
  const d = c.d;
  const lines = [];
  let subtotal = ZERO;
  for (const it of p.items) { const lt = round2(D(it.qty).times(it.unitCost)); subtotal = subtotal.plus(lt); lines.push({ it, lt }); }
  const total = subtotal.minus(p.discount);
  const paid = D(p.paidAtPurchase);
  const receivedAt = p.receivedAt;
  await c.put<T.Purchase>('purchases', {
    id: op.entityId, vendorId: p.vendorId, billNo: p.billNo ?? null, orderedAt: p.orderedAt ?? receivedAt, receivedAt, subtotal: f2(subtotal), discount: f2(p.discount), total: f2(total),
    paidAtPurchase: f2(paid), dueAmount: f2(total.minus(paid)), dueDate: p.dueDate ?? null, moneyAccountId: p.moneyAccountId ?? null, note: p.note ?? null, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id,
  });
  const made = new Map<string, string>();
  for (const { it, lt } of lines) {
    await c.put<T.PurchaseItem>('purchase_items', { id: it.id, purchaseId: op.entityId, productId: it.productId, variantId: it.variantId ?? null, batchNo: it.batchNo ?? null, expiryDate: it.expiryDate ?? null, serialNos: it.serialNos ?? [], qty: f3(it.qty), unit: it.unit, unitFactor: String(it.unitFactor), unitCost: f2(it.unitCost), lineTotal: f2(lt) });
    const pr = (await d.products.get(it.productId)) as T.Product | undefined;
    if (!pr) continue;
    const baseQty = D(it.qty).times(it.unitFactor);
    const baseCost = D(it.unitCost).div(it.unitFactor);
    if (it.batchNo || it.expiryDate || pr.hasBatches) {
      const key = `${it.productId}|${it.batchNo ?? ''}|${it.expiryDate ?? ''}`;
      const found = made.get(key) ?? (await d.stock_batches.where('productId').equals(it.productId).filter((x) => (x.batchNo ?? null) === (it.batchNo ?? null) && (x.expiryDate ?? null) === (it.expiryDate ?? null)).first())?.id;
      if (!found) {
        const id = c.next(derivedId);
        made.set(key, id);
        await c.put<T.StockBatch>('stock_batches', { id, productId: it.productId, batchNo: it.batchNo ?? null, expiryDate: it.expiryDate ?? null, costPrice: f2(baseCost), onHand: '0.000', receivedAt });
      }
    }
    for (const sn of it.serialNos ?? []) await c.put<T.SerialUnit>('serial_units', { id: c.next(derivedId), productId: it.productId, serialNo: sn, status: 'in_stock', costPrice: f2(baseCost) });
    if (pr.trackStock) c.skip(1);
    // running average cost, from the on-hand before this receipt (Bible 2.1)
    const before = D(pr.onHand).plus(pr.pendingStock ?? 0);
    const avg = averageCost(before, pr.costPrice ?? '0', baseQty, baseCost);
    await c.patch('products', pr.id, { costPrice: f2(avg) });
  }
};
const purchaseVoid: Handler<'purchase.void'> = async (c, p, op) => {
  await c.patch('purchases', op.entityId, { voidedAt: op.clientTs, voidReason: p.reason });
};
const vendorPayment: Handler<'vendor.payment'> = async (c, p, op) => {
  await c.put<T.VendorPayment>('vendor_payments', { id: op.entityId, vendorId: p.vendorId, purchaseId: p.purchaseId ?? null, amount: f2(p.amount), method: p.method, moneyAccountId: p.moneyAccountId, reference: p.reference ?? null, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  await c.addLocal<T.VendorLedgerEntry>('vendor_ledger', { id: c.localId(1), vendorId: p.vendorId, type: 'payment', amount: f2(D(p.amount).negated()), refId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs });
};
const vendorReturn: Handler<'vendor.return'> = async (c, p, op) => {
  let total = ZERO;
  for (const it of p.items) total = total.plus(round2(D(it.qty).times(it.unitCost)));
  await c.put('vendor_returns', { id: op.entityId, vendorId: p.vendorId, purchaseId: p.purchaseId ?? null, reason: p.reason, total: f2(total), settlement: p.settlement, moneyAccountId: p.moneyAccountId ?? null, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id } as never);
  for (const it of p.items) await c.put('vendor_return_items', { id: it.id, vendorReturnId: op.entityId, productId: it.productId, batchId: it.batchId ?? null, qty: f3(it.qty), unitCost: f2(it.unitCost) } as never);
  if (p.settlement === 'payable_adjust') await c.addLocal<T.VendorLedgerEntry>('vendor_ledger', { id: c.localId(1), vendorId: p.vendorId, type: 'return', amount: f2(total.negated()), refId: op.entityId, bizDate: c.bizDate, clientTs: op.clientTs });
};

// ── expenses & money ─────────────────────────────────────────────────────────
const expenseCreate: Handler<'expense.create'> = async (c, p, op) => {
  const biz = p.bizDate ?? c.bizDate;
  await c.put<T.Expense>('expenses', { id: op.entityId, categoryId: p.categoryId, seasonId: p.seasonId ?? null, amount: f2(p.amount), moneyAccountId: p.moneyAccountId, note: p.note ?? null, bizDate: biz, clientTs: op.clientTs, actorId: op.actor.id });
  await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(1), moneyAccountId: p.moneyAccountId, amount: f2(D(p.amount).negated()), reason: 'expense', refType: 'expense', refId: op.entityId, bizDate: biz, clientTs: op.clientTs, actorId: op.actor.id });
};
const expenseVoid: Handler<'expense.void'> = async (c, _p, op) => {
  await c.patch('expenses', op.entityId, { voidedAt: op.clientTs });
};
/** v10: the wrong expense is voided (money back) and the corrected one written — one op, like on the server */
const expenseEdit: Handler<'expense.edit'> = async (c, p, op) => {
  const old = (await c.d.expenses.get(op.entityId)) as T.Expense | undefined;
  if (old && !old.voidedAt) {
    await c.patch('expenses', old.id, { voidedAt: op.clientTs });
    await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(1), moneyAccountId: old.moneyAccountId, amount: f2(old.amount), reason: 'expense_void', refType: 'expense', refId: old.id, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  }
  const biz = p.bizDate ?? c.bizDate;
  await c.put<T.Expense>('expenses', { id: p.newId, categoryId: p.categoryId, seasonId: p.seasonId ?? null, amount: f2(p.amount), moneyAccountId: p.moneyAccountId, note: p.note ?? null, bizDate: biz, clientTs: op.clientTs, actorId: op.actor.id });
  await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(2), moneyAccountId: p.moneyAccountId, amount: f2(D(p.amount).negated()), reason: 'expense', refType: 'expense', refId: p.newId, bizDate: biz, clientTs: op.clientTs, actorId: op.actor.id });
};
const moneyTransfer: Handler<'money.transfer'> = async (c, p, op) => {
  await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(1), moneyAccountId: p.fromAccountId, amount: f2(D(p.amount).negated()), reason: 'transfer_out', refType: 'transfer', refId: op.opId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
  await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(2), moneyAccountId: p.toAccountId, amount: f2(p.amount), reason: 'transfer_in', refType: 'transfer', refId: op.opId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
};
const moneyAdjust: Handler<'money.adjust'> = async (c, p, op) => {
  await c.addLocal<T.MoneyMovement>('money_movements', { id: c.localId(1), moneyAccountId: p.moneyAccountId, amount: f2(p.amount), reason: p.reason, refType: 'adjust', refId: op.opId, bizDate: c.bizDate, clientTs: op.clientTs, actorId: op.actor.id });
};
const cashClose: Handler<'cash.close'> = async (c, p, op) => {
  // expected = closing balance of the day, from the movements the app knows
  const moves = await c.d.money_movements.where('moneyAccountId').equals(p.moneyAccountId).toArray();
  const expected = moves.filter((m) => m.bizDate <= p.bizDate).reduce((a, m) => a.plus(m.amount), ZERO);
  await c.put<T.CashClosing>('cash_closings', { id: op.entityId, moneyAccountId: p.moneyAccountId, bizDate: p.bizDate, expected: f2(expected), counted: f2(p.counted), difference: f2(D(p.counted).minus(expected)), note: p.note ?? null, closedById: op.actor.id });
};

// ── small entities ───────────────────────────────────────────────────────────
const noteUpsert: Handler<'note.upsert'> = async (c, p, op) => {
  const ex = (await c.d.notes.get(op.entityId)) as T.Note | undefined;
  await c.put<T.Note>('notes', { id: op.entityId, text: p.text, remindAt: p.remindAt ?? null, actorId: ex?.actorId ?? op.actor.id, deletedAt: p.deleted ? op.clientTs : null });
};
const repairUpsert: Handler<'repair.upsert'> = async (c, p, op) => {
  const ex = (await c.d.repair_jobs.get(op.entityId)) as T.RepairJob | undefined;
  await c.put<T.RepairJob>('repair_jobs', { ...(ex ?? { id: op.entityId, createdAt: op.clientTs }), customerId: p.customerId ?? null, customerName: p.customerName ?? null, customerPhone: p.customerPhone ?? null, deviceName: p.deviceName, problem: p.problem, status: p.status, charge: f2(p.charge), advance: f2(p.advance), promisedAt: p.promisedAt ?? null, deliveredAt: p.deliveredAt ?? null, saleId: p.saleId ?? null, note: p.note ?? null, deletedAt: p.deleted ? op.clientTs : null });
};
const serviceRateUpsert: Handler<'service_rate.upsert'> = async (c, p, op) => {
  await c.put<T.ServiceRate>('service_rates', { id: op.entityId, name: p.name, rate: f2(p.rate), unitLabel: p.unitLabel, isActive: p.isActive, deletedAt: p.deleted ? op.clientTs : null });
};
const bundleUpsert: Handler<'bundle.upsert'> = async (c, p, op) => {
  await c.put<T.Bundle>('bundles', { id: op.entityId, name: p.name, className: p.className ?? null, items: p.items.map((i) => ({ productId: i.productId, qty: String(i.qty) })), isActive: p.isActive, deletedAt: p.deleted ? op.clientTs : null });
};
const fieldDefUpsert: Handler<'field_def.upsert'> = async (c, p, op) => {
  await c.put<T.FieldDef>('field_defs', { id: op.entityId, key: p.key, label: p.label, type: p.type, options: p.options ?? null, order: p.order, deletedAt: p.deleted ? op.clientTs : null });
};

type CoreOp = Exclude<OpType, ModuleOpType>;
type HandlerMap = { [K in OpType]: Handler<K> };
const CORE_APPLY: { [K in CoreOp]: Handler<K> } = {
  'sale.create': saleCreate, 'sale.void': saleVoid, 'sale.restore': saleRestore, 'return.create': returnCreate, 'return.void': returnVoid,
  'baki.credit': bakiCredit, 'baki.payment': bakiPayment, 'baki.writeoff': bakiWriteoff, 'customer.upsert': customerUpsert, 'customer.delete': customerDelete,
  'product.upsert': productUpsert, 'product.delete': productDelete, 'stock.adjust': stockAdjust, 'purchase.create': purchaseCreate, 'purchase.void': purchaseVoid,
  'vendor.upsert': vendorUpsert, 'vendor.payment': vendorPayment, 'vendor.return': vendorReturn, 'expense.create': expenseCreate, 'expense.void': expenseVoid, 'expense.edit': expenseEdit,
  'money.transfer': moneyTransfer, 'money.adjust': moneyAdjust, 'cash.close': cashClose, 'note.upsert': noteUpsert, 'repair.upsert': repairUpsert,
  'service_rate.upsert': serviceRateUpsert, 'bundle.upsert': bundleUpsert, 'field_def.upsert': fieldDefUpsert,
};
export const APPLY = { ...CORE_APPLY, ...MODULE_APPLY } as unknown as HandlerMap;

/** op type → the mirror table whose pulled rows must not overwrite an unacknowledged local edit */
export const GUARDS: Partial<Record<OpType, string>> = {
  'customer.upsert': 'customers', 'customer.delete': 'customers', 'product.upsert': 'products', 'product.delete': 'products', 'vendor.upsert': 'vendors',
  'note.upsert': 'notes', 'repair.upsert': 'repair_jobs', 'service_rate.upsert': 'service_rates', 'bundle.upsert': 'bundles', 'field_def.upsert': 'field_defs',
  'sale.create': 'sales', 'sale.void': 'sales', 'sale.restore': 'sales', 'return.create': 'sale_returns', 'return.void': 'sale_returns',
  'purchase.void': 'purchases', 'expense.void': 'expenses', 'expense.edit': 'expenses', ...MODULE_GUARDS,
};

void diffDays;
